import { mergeStyles } from "@fluentui/react/lib/Styling";
// Design tokens
export const designTokens = {
    colors: {
        primary: "#0078d4",
        primaryLight: "#f3f9fd",
        primaryLighter: "#f3f9ff",
        border: "#d1d1d1",
        borderLight: "#e1e1e1",
        backgroundHover: "#f5f5f5",
        backgroundSecondary: "#edebe9",
        backgroundSecondaryHover: "#e1dfdd",
        text: "#323130",
        textSecondary: "#605e5c",
        white: "#ffffff",
    },
    spacing: {
        xs: "4px",
        sm: "8px",
        md: "12px",
        lg: "16px",
        xl: "20px",
        xxl: "24px",
    },
    borderRadius: "4px",
    transitions: {
        default: "all 0.2s ease",
    },
    shadows: {
        hover: "0 2px 8px rgba(0, 0, 0, 0.1)",
    },
    fontSize: {
        xs: "12px",
        sm: "14px",
        md: "16px",
    },
};
// Stack tokens
export const stackTokens = {
    section: { childrenGap: 20 },
    formRow: { childrenGap: 4 },
};
// Container styles
export const containerStyles = {
    tilesContainer: mergeStyles({
        display: "flex",
        flexWrap: "wrap",
        gap: designTokens.spacing.lg,
    }),
    incidentTilesContainer: mergeStyles({
        marginTop: designTokens.spacing.sm,
    }),
    escalationReasonsContainer: mergeStyles({
        display: "flex",
        flexWrap: "wrap",
        gap: designTokens.spacing.md,
        alignItems: "flex-start",
    }),
    tileContainer: mergeStyles({
        display: "flex",
        gap: designTokens.spacing.md,
        flexWrap: "wrap",
        marginTop: designTokens.spacing.md,
    }),
};
// Tile styles
export const tileStyles = {
    // Main call type tiles
    callTypeTile: (isSelected) => mergeStyles({
        border: isSelected
            ? `2px solid ${designTokens.colors.primary}`
            : `1px solid ${designTokens.colors.border}`,
        borderRadius: designTokens.borderRadius,
        padding: `${designTokens.spacing.md} ${designTokens.spacing.lg}`,
        cursor: "pointer",
        backgroundColor: isSelected
            ? designTokens.colors.primaryLight
            : designTokens.colors.white,
        transition: designTokens.transitions.default,
        minHeight: "50px",
        width: "150px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        ":hover": {
            borderColor: designTokens.colors.primary,
            backgroundColor: isSelected
                ? designTokens.colors.primaryLight
                : designTokens.colors.backgroundHover,
            boxShadow: designTokens.shadows.hover,
        },
        ":focus": {
            outline: `2px solid ${designTokens.colors.primary}`,
            outlineOffset: "2px",
        },
    }),
    // Unified style for selection tiles (Technical Depth, Escalation Reasons, Workloads, Components)
    unifiedTile: (isSelected) => mergeStyles({
        border: isSelected
            ? `2px solid ${designTokens.colors.primary}`
            : `2px solid ${designTokens.colors.borderLight}`,
        borderRadius: designTokens.borderRadius,
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.xxl}`,
        cursor: "pointer",
        backgroundColor: isSelected
            ? designTokens.colors.primaryLighter
            : designTokens.colors.white,
        transition: designTokens.transitions.default,
        minHeight: "36px",
        minWidth: "180px",
        maxWidth: "320px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        ":hover": {
            borderColor: designTokens.colors.primary,
            boxShadow: designTokens.shadows.hover,
        },
    }),
    tileHeader: mergeStyles({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: designTokens.spacing.xs,
        marginBottom: "0px",
    }),
    tileIcon: (isSelected) => mergeStyles({
        fontSize: designTokens.fontSize.md,
        color: isSelected
            ? designTokens.colors.primary
            : designTokens.colors.textSecondary,
    }),
    selectionTileContent: mergeStyles({
        alignItems: "center",
        display: "flex",
        gap: "12px",
        minWidth: 0,
    }),
    selectionTileIconContainer: (backgroundColor) => mergeStyles({
        alignItems: "center",
        backgroundColor,
        borderRadius: "50%",
        display: "flex",
        flexShrink: 0,
        height: "40px",
        justifyContent: "center",
        width: "40px",
    }),
    selectionTileIcon: (color) => mergeStyles({
        color,
        fontSize: "20px",
    }),
    selectionTileImage: mergeStyles({
        height: "24px",
        width: "24px",
    }),
    tileTitle: (isSelected) => mergeStyles({
        fontSize: designTokens.fontSize.xs,
        fontWeight: 600,
        color: isSelected
            ? designTokens.colors.primary
            : designTokens.colors.text,
        textAlign: "center",
    }),
    tileSubTitle: mergeStyles({
        fontSize: "10px",
        fontWeight: 400,
        color: designTokens.colors.textSecondary,
        textAlign: "center",
        marginTop: designTokens.spacing.xs,
        lineHeight: "12px",
    }),
    otherTileContent: mergeStyles({
        cursor: "pointer",
    }),
};
// Section header styles
export const sectionStyles = {
    header: mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: designTokens.spacing.sm,
        cursor: "pointer",
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
        backgroundColor: designTokens.colors.backgroundSecondary,
        border: `1px solid ${designTokens.colors.border}`,
        borderRadius: designTokens.borderRadius,
        marginTop: designTokens.spacing.md,
        fontSize: designTokens.fontSize.sm,
        ":hover": {
            backgroundColor: designTokens.colors.backgroundSecondaryHover,
        },
    }),
    headerComplete: mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: designTokens.spacing.sm,
        cursor: "pointer",
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
        backgroundColor: "#dff6dd",
        border: "1px solid #107c10",
        borderRadius: designTokens.borderRadius,
        marginTop: designTokens.spacing.md,
        fontSize: designTokens.fontSize.sm,
        color: "#107c10",
        ":hover": {
            backgroundColor: "#c8e6c9",
        },
    }),
};
// Common component patterns
export const commonStyles = {
    // Section containers
    sectionContainer: mergeStyles({
        marginTop: designTokens.spacing.lg,
    }),
    sectionContainerMedium: mergeStyles({
        marginTop: designTokens.spacing.md,
    }),
    sectionGroup: mergeStyles({
        marginTop: designTokens.spacing.lg,
    }),
    // Form fields
    textField: mergeStyles({
        marginBottom: designTokens.spacing.md,
    }),
    customerField: mergeStyles({
        marginBottom: designTokens.spacing.md,
    }),
    autoTypingField: mergeStyles({
        marginBottom: designTokens.spacing.md,
        position: "relative",
    }),
    otherTextField: mergeStyles({
        marginTop: designTokens.spacing.sm,
        maxWidth: "300px",
    }),
    // Text styles
    sectionTitle: mergeStyles({
        fontSize: designTokens.fontSize.md,
        fontWeight: 600,
        color: designTokens.colors.text,
        marginBottom: designTokens.spacing.sm,
    }),
    subsectionTitle: mergeStyles({
        fontSize: designTokens.fontSize.sm,
        fontWeight: 600,
        color: designTokens.colors.text,
        marginTop: designTokens.spacing.lg,
        marginBottom: designTokens.spacing.sm,
    }),
    description: mergeStyles({
        fontSize: designTokens.fontSize.sm,
        color: designTokens.colors.textSecondary,
        marginBottom: designTokens.spacing.md,
    }),
    fieldDescription: mergeStyles({
        fontSize: designTokens.fontSize.xs,
        color: designTokens.colors.textSecondary,
        marginBottom: designTokens.spacing.xs,
    }),
    validationMessage: mergeStyles({
        fontSize: designTokens.fontSize.xs,
        marginTop: designTokens.spacing.xs,
    }),
    // Layout containers
    loadingContainer: mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: designTokens.spacing.sm,
        marginTop: designTokens.spacing.sm,
    }),
    submitButtonContainer: mergeStyles({
        display: "flex",
        justifyContent: "flex-start",
        marginTop: designTokens.spacing.xl,
    }),
    // Buttons
    submitButton: mergeStyles({
        marginTop: designTokens.spacing.xl,
        alignSelf: "flex-start",
    }),
    // Special sections
    optionalSection: mergeStyles({
        marginTop: designTokens.spacing.md,
        padding: designTokens.spacing.md,
        border: `1px solid ${designTokens.colors.borderLight}`,
        borderRadius: designTokens.borderRadius,
        backgroundColor: "#fafafa",
    }),
    optionalSubsectionHeader: mergeStyles({
        display: "flex",
        alignItems: "center",
        gap: designTokens.spacing.sm,
        cursor: "pointer",
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
        backgroundColor: designTokens.colors.backgroundSecondary,
        border: `1px solid ${designTokens.colors.borderLight}`,
        borderRadius: designTokens.borderRadius,
        marginTop: designTokens.spacing.md,
        fontSize: designTokens.fontSize.sm,
        ":hover": {
            backgroundColor: designTokens.colors.backgroundSecondaryHover,
        },
    }),
    // Info text
    emptyState: mergeStyles({
        padding: designTokens.spacing.lg,
        textAlign: "center",
        color: designTokens.colors.textSecondary,
        fontSize: designTokens.fontSize.sm,
    }),
    outageInfo: mergeStyles({
        marginTop: designTokens.spacing.sm,
        fontSize: designTokens.fontSize.sm,
        color: designTokens.colors.textSecondary,
    }),
};
// Main container style
export const mainContainerStyle = {
    root: {
        padding: designTokens.spacing.xl,
    },
};
